import { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faCheck, faXmark } from "@fortawesome/free-solid-svg-icons";
import { parsePercentageInput } from "utils/percentage";
import "./percentageCell.scss";

/**
 * The commission rate for one driver or one restaurant, editable in place.
 *
 * SET AND UNSET MUST LOOK DIFFERENT. Most rows have no rate of their own — 62
 * of the 95 active drivers and 223 of the 301 restaurants — so a bare number
 * would tell an operator nothing about whether it was agreed with that person
 * or inherited from the city. An unset row shows the same effective figure,
 * muted, with «افتراضي» beside it. A set row shows a plain, solid number.
 *
 * That distinction is the reason the columns stayed nullable rather than being
 * backfilled: `null` means "on the city default" and it is worth seeing.
 *
 * The value is written straight from the table because a rate is a single
 * number an operator adjusts often, and sending them to an edit page to change
 * one field is how it stops being adjusted.
 */
const PercentageCell = ({ ownValue, effectiveValue, onSave, disabled = false }) => {
    const isSet = ownValue !== null && ownValue !== undefined && ownValue !== "";
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState("");
    const [saving, setSaving] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        if (editing && inputRef.current) inputRef.current.focus();
    }, [editing]);

    const open = () => {
        // Pre-fills the effective figure, so an operator confirming the default
        // types nothing — the common case for most rows.
        setDraft(isSet ? String(ownValue) : String(effectiveValue ?? ""));
        setEditing(true);
    };

    const cancel = () => {
        setEditing(false);
        setDraft("");
    };

    const save = async () => {
        const parsed = parsePercentageInput(draft);
        // An empty box means "clear it and go back to the city default", which
        // is a real action and not an error.
        const next = draft.trim() === "" ? null : parsed;

        if (draft.trim() !== "" && next === null) return;

        setSaving(true);
        try {
            await onSave(next);
            setEditing(false);
        } finally {
            setSaving(false);
        }
    };

    if (editing) {
        return (
            <td className="percentage-cell">
                <div className="percentage-cell__edit">
                    <input
                        ref={inputRef}
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={draft}
                        disabled={saving}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") save();
                            if (e.key === "Escape") cancel();
                        }}
                        placeholder="فارغ = الافتراضي"
                        aria-label="النسبة"
                    />
                    <button type="button" onClick={save} disabled={saving} title="حفظ">
                        <FontAwesomeIcon icon={faCheck} />
                    </button>
                    <button type="button" onClick={cancel} disabled={saving} title="إلغاء">
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </div>
            </td>
        );
    }

    return (
        <td className="percentage-cell">
            <button
                type="button"
                className={`percentage-cell__value${isSet ? "" : " percentage-cell__value--default"}`}
                onClick={open}
                disabled={disabled}
                title={
                    isSet ? "نسبة خاصة — اضغط للتعديل" : "لم تُحدَّد — القيمة من إعدادات المدينة"
                }
            >
                <span className="percentage-cell__number">
                    {Number(isSet ? ownValue : (effectiveValue ?? 0)).toFixed(2)}%
                </span>
                {isSet ? null : <span className="percentage-cell__badge">افتراضي</span>}
                <FontAwesomeIcon icon={faPen} size="xs" />
            </button>
        </td>
    );
};

export default PercentageCell;
